import React, { useEffect, useState } from 'react'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'
import Grid from '@material-ui/core/Grid'
import Card from '@material-ui/core/Card'
import CardContent from '@material-ui/core/CardContent'
import DashboardIcon from '@material-ui/icons/ViewQuilt'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableHead from '@material-ui/core/TableHead'
import Select from '@material-ui/core/Select'
import FormControl from '@material-ui/core/FormControl'
import InputLabel from '@material-ui/core/InputLabel'
import MenuItem from '@material-ui/core/MenuItem'
import TableRow from '@material-ui/core/TableRow'
import LinearProgress from '@material-ui/core/LinearProgress'
import DonutChart from '../../components/DonutChart'
import TreemapChart from '../../components/TreemapChart'
import HealthIndicator from '../../components/HealthIndicator'
import TimeIndicator from '../../components/TimeIndicator'
import * as api from '../../data'

const useStyles = makeStyles(theme => ({
  stretch: {
    height: '100%',
    width: '100%'
  },

  table: {
    minWidth: 650
  },

  formControl: {
    width: '240px',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  }
}))

const StatusTable = ({ data, title }) => {
  const classes = useStyles()

  return (
    <Card>
      {data.length === 0 && <LinearProgress />}
      <CardContent>
        <Typography style={{ paddingBottom: 8 }} align='left'>
          {title}
        </Typography>

        <TableContainer>
          <Table
            size='small'
            className={classes.table}
            aria-label='status table'
          >
            <TableHead>
              <TableRow hover>
                <TableCell padding='none'>
                  <Typography>Status</Typography>
                </TableCell>
                <TableCell padding='none'>
                  <Typography>Site</Typography>
                </TableCell>
                <TableCell padding='none'>
                  <Typography>Serial</Typography>
                </TableCell>
                <TableCell>
                  <Typography>Vendor</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data
                .sort((a, b) =>
                  a.healthStatusColor < b.healthStatusColor ? -1 : 1
                )

                .map(item => (
                  <TableRow
                    href={`/#/modalities/${item.id}/show`}
                    hover
                    key={'row' + item.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() =>
                      (window.location.href = `/#/modalities/${item.id}/show`)
                    }
                  >
                    <TableCell padding='none'>
                      <HealthIndicator {...item} />
                    </TableCell>
                    <TableCell padding='none'>{item.siteName}</TableCell>
                    <TableCell padding='none'>{item.serialNumber}</TableCell>
                    <TableCell>
                      {item.manufacturer} {item.model}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  )
}

const StatusCard = ({ title, value, color }) => {
  value = value || 0

  return (
    <Card style={{ padding: 0 }}>
      {!value && value !== 0 && <LinearProgress />}
      <CardContent
        style={{
          borderRadius: 8,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'center',
          height: 120,
          paddingTop: 0
        }}
      >
        <Typography style={{ paddingTop: 18 }} align='center'>
          {title}
        </Typography>

        <Typography
          variant='h3'
          align='center'
          component='div'
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            fontWeight: 'bold',
            color: color,
            width: '100%',
            borderRadius: 8,
            marginTop: 0
          }}
        >
          <div>{value}</div>
        </Typography>
      </CardContent>
    </Card>
  )
}

// Utility function to count device statuses
const countDeviceStatus = (acc, item) => {
  // Increment manufacturer and model counts
  acc.manufacturers[item.manufacturer] = (acc.manufacturers[item.manufacturer] || 0) + 1
  acc.models[item.model] = (acc.models[item.model] || 0) + 1

  // Count devices based on timeStatusColor - each device counted only once
  if (item.timeStatusColor === 'green') {
    // Online devices: count based on healthStatusColor
    acc[item.healthStatusColor] = (acc[item.healthStatusColor] || 0) + 1
  } else {
    // Offline devices: count as offline (any non-green timeStatusColor)
    acc.offline = (acc.offline || 0) + 1
  }

  // Add to status list for flagged devices (health issues on online devices)
  if (['yellow', 'red'].includes(item.healthStatusColor) && item.timeStatusColor === 'green') {
    acc.status.push(item)
  }
}

const getChartData = data => {
  const res = data.reduce(
    (acc, item) => {
      countDeviceStatus(acc, item)
      return acc
    },
    {
      manufacturers: {},
      models: {},
      status: [],
      offline: null
    }
  )

  return res
}
const DeviceSample = () => {
  const classes = useStyles()
  const [data, setData] = useState([])
  const [type, setType] = useState(null)
  const [chartData, setChartData] = useState({
    manufacturers: {},
    models: {},
    status: [],
    offline: null
  })

  useEffect(() => {
    fetch()
  }, [])

  useEffect(() => {
    if (!type) fetch()
    else fetch({ type })
    setData([])
  }, [type])

  useEffect(() => {
    const chartData = getChartData(data)
    setChartData(chartData)
  }, [data])

  const fetch = async (filter = {}) => {
    filter = { active: 1, ...filter }
    const data = await api.modalities.fetch({ filter: JSON.stringify(filter) })
    setData(data)
  }

  return (
    <Box mt={2} mb={4} width='100%'>
      <Box display='flex' justifyContent='flex-end'>
        <FormControl
          fullWidth
          size='small'
          variant='filled'
          className={classes.formControl}
        >
          <InputLabel htmlFor='type'>Filter By Device Type</InputLabel>
          <Select
            id='type'
            value={type}
            onChange={event => setType(event.target.value)}
          >
            <MenuItem value=''>
              <span style={{ visibility: 'hidden' }}>None</span>
            </MenuItem>
            <MenuItem value='1'>CT</MenuItem>
            <MenuItem value='2'>MRI</MenuItem>
            <MenuItem value='5'>PETCT</MenuItem>
            <MenuItem value='6'>Patient Monitor</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={1}>
        <Grid item md={12} lg={12}>
          <Grid container spacing={1}>
            <Grid item md={12} lg={12}>
              <Grid container spacing={1} justify='center'>
                <Grid item xs={10} sm={12} md={5} lg={6}>
                  <DonutChart
                    id='device-manufacturers'
                    title='Device Manufacturers'
                    data={Object.entries(chartData.manufacturers).map(
                      ([name, value]) => ({ name, value })
                    )}
                  />
                </Grid>

                <Grid item xs={10} sm={12} md={7} lg={6}>
                  <TreemapChart
                    id='device-model'
                    title='Device Models'
                    data={Object.entries(chartData.models).map(
                      ([name, value]) => ({
                        name,
                        value
                      })
                    )}
                  />
                </Grid>

                <Grid item xs={5} sm={6} md={3} lg={3}>
                  <StatusCard
                    title='Green'
                    value={chartData.green}
                    color='#66bb6a'
                  />
                </Grid>

                <Grid item xs={5} sm={6} md={3} lg={3}>
                  <StatusCard
                    title='Yellow Flagged'
                    value={chartData.yellow}
                    color='#fdd835'
                  />
                </Grid>

                <Grid item xs={5} sm={6} md={3} lg={3}>
                  <StatusCard
                    title='Red Flagged'
                    value={chartData.red}
                    color={`${chartData.red ? '#ff5722' : '#99999940'}`}
                  />
                </Grid>

                <Grid item xs={5} sm={6} md={3} lg={3}>
                  <StatusCard
                    title='Offline'
                    value={chartData.offline}
                    color={`${chartData.offline ? '#ff5722' : '#99999940'}`}
                  />
                </Grid>

                <Grid item xs={10} sm={12} md={12} lg={12}>
                  <StatusTable
                    title={'Impacted Devices'}
                    data={chartData.status}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  )
}

export default DeviceSample
export const ModalityDashboardIcon = DashboardIcon
